<script setup lang="ts">
import VarsityInlineConfirm from "@/components/VarsityInlineConfirm.vue"
import VarsityInlineConfirmButton from "@/components/VarsityInlineConfirmButton.vue"
import { createInlineConfirm } from "@/composables/createInlineConfirm"
import type { ChampionshipRegistrationSelectTeamTask } from "@/model/championship/championship-registration-select-team-task"
import type { TasksProps } from "@/pages/Tasks/Tasks/tasks-props"
import { useHttpClient, useHttpGet } from "@/vf"
import AnimateIf from "@/vf/components/AnimateIf.vue"
import { ref, toRefs } from "vue"
import ChampionshipRegistrationEditContactPerson from "./ChampionshipRegistrationEditContactPerson.vue"
import ChampionshipRegistrationEditDebitorProfile from "./ChampionshipRegistrationEditDebitorProfile.vue"
import ChampionshipRegistrationNewTeamWizard from "./ChampionshipRegistrationNewTeamWizard.vue"
import ChampionshipRegistrationTeamList from "./ChampionshipRegistrationTeamList.vue"
import type { ChampionshipRegistrationSelectTeamData } from "./championship-registration"
const props = defineProps<TasksProps<ChampionshipRegistrationSelectTeamTask>>()

const emit = defineEmits<{
    (e: "updated"): void
}>()

const { task } = toRefs(props)
const http = useHttpClient()
const whatToDo = ref<"new_team" | "change_contact_person" | "change_debitor_profile" | null>(null)

/*─────────────────────────────────────┐
│  list                                │
└─────────────────────────────────────*/
const { data, refresh } = await useHttpGet<ChampionshipRegistrationSelectTeamData>(
    `/championship/registration/${task.value.organizationId}/select-team/${task.value.id}`,
)

/*─────────────────────────────────────┐
│  refresh list                        │
└─────────────────────────────────────*/
async function onUpdated() {
    refresh()
    whatToDo.value = null
}

/*─────────────────────────────────────┐
│  finalize                            │
└─────────────────────────────────────*/
async function finalizeRegistration() {
    await http.post(`/championship/registration/${data.value.registrationId}/finalize`)
    emit("updated")
}

/*─────────────────────────────────────┐
│  cancel registration                 │
└─────────────────────────────────────*/
const cancelRegistrationConfirm = createInlineConfirm({
    action: async () => {
        await http.post(`/championship/registration/${data.value.registrationId}/cancel`)
        emit("updated")
    },
    afterAction: () => {
        emit("updated")
    },
})
</script>
<template>
    <div v-if="task.state === 'open'">
        <div class="p-5 form-light" v-if="!data.contactPerson.contactPersonName">
            <b>
                {{ $t("@tasks:tasks.championship_registration.wizard.provide_contact_person_first") }}
            </b>
            <ChampionshipRegistrationEditContactPerson v-bind="{ data }" @updated="onUpdated" />
        </div>
        <div class="p-5 form-light" v-else-if="!data.debitorProfile">
            <b>
                {{ $t("@tasks:tasks.championship_registration.wizard.provide_debitor_profile_first") }}
            </b>
            <ChampionshipRegistrationEditDebitorProfile v-bind="{ data }" @updated="onUpdated" />
        </div>
        <div class="p-5 form-light" v-else>
            <b>{{ $t("@tasks:tasks.championship_registration.description.title") }}</b>
            <div class="mb-4">{{ $t("@tasks:tasks.championship_registration.description.text") }}</div>

            <div class="my-5 pb-3">
                <b class="d-block mt-4">{{ $t("@tasks:tasks.championship_registration.wizard.what_to_do") }}</b>

                <button
                    type="button"
                    class="wizard-button"
                    @click="whatToDo = 'new_team'"
                    :class="{ dark: whatToDo === 'new_team' }"
                >
                    {{ $t("@tasks:tasks.championship_registration.wizard.register_team") }}
                </button>

                <AnimateIf :if="whatToDo === 'new_team'">
                    <ChampionshipRegistrationNewTeamWizard v-bind="{ data }" @saved="onUpdated" />
                </AnimateIf>

                <button
                    type="button"
                    class="wizard-button"
                    @click="whatToDo = 'change_contact_person'"
                    :class="{ dark: whatToDo === 'change_contact_person' }"
                >
                    {{ $t("@tasks:tasks.championship_registration.wizard.change_contact_person") }}
                </button>

                <AnimateIf :if="whatToDo === 'change_contact_person'">
                    <ChampionshipRegistrationEditContactPerson v-bind="{ data }" @updated="onUpdated" />
                </AnimateIf>

                <button
                    type="button"
                    class="wizard-button"
                    @click="whatToDo = 'change_debitor_profile'"
                    :class="{ dark: whatToDo === 'change_debitor_profile' }"
                >
                    {{ $t("@tasks:tasks.championship_registration.wizard.change_debitor_profile") }}
                </button>

                <AnimateIf :if="whatToDo === 'change_debitor_profile'">
                    <div class="p-5 form-light">
                        <b>
                            {{ $t("@tasks:tasks.championship_registration.wizard.please_provide_new_debitor_profile") }}
                        </b>
                        <ChampionshipRegistrationEditDebitorProfile v-bind="{ data }" @updated="onUpdated" />
                    </div>
                </AnimateIf>

                <button
                    class="wizard-button"
                    type="button"
                    v-if="data.teams.length > 0"
                    @click="finalizeRegistration()"
                >
                    {{ $t("@tasks:tasks.championship_registration.wizard.finalize_registration") }}
                </button>
            </div>

            <b class="mt-5 d-block">
                {{ $t("@tasks:tasks.championship_registration.contact_person.text", data.contactPerson) }}
            </b>
            <b class="my-2 d-block">
                {{ $t("@tasks:tasks.championship_registration.debitor_profile.text", { ...data.debitorProfile }) }}
            </b>

            <ChampionshipRegistrationTeamList
                :teams="data.teams"
                :show-price="false"
                :show-edit="true"
                :show-delete="true"
                :data="data"
                :view-mode="props.viewMode"
                @updated="refresh()"
            />

            <div class="d-flex justify-content-between mt-5">
                <VarsityInlineConfirmButton :controller="cancelRegistrationConfirm">
                    {{ $t("@tasks:tasks.championship_registration.wizard.cancel_registration.button") }}
                </VarsityInlineConfirmButton>

                <button
                    class="btn btn-primary"
                    type="button"
                    :disabled="data.teams.length === 0"
                    @click="finalizeRegistration()"
                >
                    {{ $t("@tasks:tasks.championship_registration.wizard.finalize_registration") }}
                </button>
            </div>

            <VarsityInlineConfirm :controller="cancelRegistrationConfirm">
                <template #confirmation>
                    {{ $t("@tasks:tasks.championship_registration.wizard.cancel_registration.confirmation") }}
                </template>
                <template #success>
                    {{ $t("@tasks:tasks.championship_registration.wizard.cancel_registration.success") }}
                </template>
            </VarsityInlineConfirm>
        </div>
    </div>
</template>
